/**
 * Compliance — the landlord businesses waiting to be listed to tenants.
 *
 * THE ERRAND THIS EXISTS FOR, in the founder's words on 16 September 2026:
 *
 *   "as a landlord, I asked to be listed, but nowhere in the portal do I see
 *    anything that needs to be approved so that the landlord's 'business' can
 *    show up in searches."
 *
 * He was right, and it was worse than a missing screen.
 *
 * `landlord_portfolios` carries both halves of Immutable Rule 4 — eligibility
 * derived from verified compliance, never declared. `is_listed` is the landlord
 * asking and was fully built. `approved_at` is Orchid verifying, through
 * `fn_admin_review_portfolio`, which NOTHING in any repository called. `visible`
 * is both, so a landlord who asked to be listed waited on a switch no screen
 * could move.
 *
 * AND THE FUNCTION COULD NEVER HAVE SUCCEEDED ANYWAY. Driving it on a real row
 * raised 23503: `approved_by` referenced `profiles(id)` — the CONSUMER identity
 * table — while the approver is staff. Canon 1.2 written into a foreign key,
 * and invisible for exactly as long as nothing called the function. Platform
 * script 343 repairs that and names the permission.
 *
 * THIS IS THE SECOND TIME. `OwnershipQueue` in this same folder opens with the
 * same finding about title deeds: both halves enforced in the database, neither
 * reachable from here. A platform RPC whose only legitimate caller is the
 * Portal is invisible to every reachability guard in either repository — they
 * run app→database and database→app within the platform alone.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE DESK IS ACTUALLY DECIDING
 *
 * "Every unit must be proven owned before it can operate. Proof is recorded by
 * a compliance seat, never claimed by the landlord."
 *
 * So the figure that matters is PROVEN UNITS AGAINST TOTAL UNITS, and the queue
 * carries both rather than one "units" number that hides the difference. A
 * business asking to be listed with nothing proven owned is the case a reviewer
 * must see without reading anything else.
 *
 * IT DECIDES NOTHING FOR THEM. No tick, no colour, no "looks fine" on a
 * business with a full set of proven units. Immutable Rule 4 at the last place
 * it could be broken: a verdict rendered on a compliance screen carries that
 * desk's authority without its judgement. The counts are stated; the decision
 * is the reviewer's.
 *
 * NO NAMES. The business name, its cities, its units, its OFX code — never the
 * landlord's personal name. Prohibition 6, and the same choice OwnershipQueue
 * makes about `owner_name`.
 *
 * AND THERE IS A REASON BOX NOW, which this header used to explain the absence
 * of. It said: "a text box whose contents were discarded would read to a
 * reviewer as though their words were kept ... If a reason belongs on the
 * record, that is a platform migration, not a field here." Platform script 351
 * IS that migration. The permission has declared `requires_reason_capture`
 * since 343 — which is what renders the red pill at the top of this card — over
 * a function that could not store one, and the screen had to apologise for it.
 *
 * AND A DESK THAT CAN ONLY SAY YES IS NOT A DESK. Before 351 the only acts were
 * verify and un-verify, so a business that must NOT be listed could only be LEFT
 * in the queue, for ever, with no record that anybody had looked at it. Decline
 * is the missing verb.
 */

import { useEffect, useState } from 'react';
import {
  O, SANS, Card, StatusPill, Notice, PrimaryCTA, QuietButton, Spinner,
} from '../ui';
import { fetchLandlordBusinessQueue, decideLandlordBusiness } from '../lib/compliance';

/**
 * Units proven against units held, as a sentence rather than a verdict.
 *
 * The tone is the ONLY thing this reads into the numbers, and it is deliberately
 * coarse: "none proven" is a fact a reviewer must not miss, and everything else
 * is quiet. It does not shade towards approval as the ratio improves, because
 * that would be the screen leaning on the decision.
 */
const Proven = ({ proven, total }) => {
  const none = !proven;
  return (
    <span style={{
      fontFamily: SANS, fontSize: 12, color: none ? O.critical : O.inkMuted,
      fontWeight: none ? 600 : 400,
    }}>
      {total === 0
        ? 'No units on this business yet'
        : `${proven} of ${total} unit${total === 1 ? '' : 's'} proven owned`}
    </span>
  );
};

const Row = ({ business, onDecide, busy, reason, onReason }) => {
  const approved = Boolean(business.approved_at);
  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
        marginBottom: 8,
      }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <p style={{
            fontFamily: SANS, fontSize: 14, fontWeight: 700, color: O.ink,
            margin: '0 0 2px',
          }}>
            {business.portfolio_name || 'Unnamed business'}
          </p>
          <p style={{ fontFamily: SANS, fontSize: 11.5, color: O.inkFaint, margin: 0 }}>
            {/* The OFX code identifies the account without naming a person. It
                can be absent on an account that has not completed its identity,
                and a dash is the honest rendering of that. */}
            {business.ofx_connect_code || '— no OFX code on this account yet'}
          </p>
        </div>
        <StatusPill tone={approved ? 'good' : 'quiet'}>
          {approved ? 'Listed' : 'Asked to be listed'}
        </StatusPill>
      </div>

      {business.about && (
        <p style={{
          fontFamily: SANS, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted,
          margin: '0 0 8px', maxWidth: '70ch',
        }}>
          {business.about}
        </p>
      )}

      <div style={{ marginBottom: 10 }}>
        <Proven proven={business.proven_units} total={business.total_units} />
        {business.cities?.length > 0 && (
          <span style={{ fontFamily: SANS, fontSize: 12, color: O.inkFaint }}>
            {' · '}{business.cities.join(', ')}
          </span>
        )}
      </div>

      {/* WHAT WAS DECIDED LAST, AND WHY.
          A seat coming to this card second should see that somebody has already
          looked at it and what they said, or they will make the decision again
          from nothing. A declined business stays on this desk for exactly that
          reason — it is no longer listed and no longer asking, and without this
          line it would simply have vanished. */}
      {business.last_decision && (
        <p style={{
          fontFamily: SANS, fontSize: 12, lineHeight: 1.6, color: O.inkMuted,
          margin: '0 0 10px', paddingLeft: 10,
          borderLeft: `2px solid ${O.hairlineOn}`,
        }}>
          <strong style={{ color: O.ink, textTransform: 'capitalize' }}>
            {business.last_decision}
          </strong>
          {' — '}{business.last_reason}
        </p>
      )}

      {/* THE REASON, ABOVE THE BUTTONS AND NOT BELOW THEM.
          A field under a control people have already pressed is a field nobody
          reads. It is one box for all three decisions because it is one
          sentence about one judgement, whichever way that judgement went. */}
      <label
        htmlFor={`reason-${business.portfolio_id}`}
        style={{
          display: 'block', fontFamily: SANS, fontSize: 11, fontWeight: 600,
          letterSpacing: '0.07em', textTransform: 'uppercase',
          color: O.inkMuted, marginBottom: 6,
        }}
      >
        Your reason
      </label>
      <textarea
        id={`reason-${business.portfolio_id}`}
        value={reason || ''}
        disabled={busy}
        onChange={(e) => onReason(business.portfolio_id, e.target.value)}
        rows={2}
        placeholder="What you checked, and what it showed."
        style={{
          width: '100%', boxSizing: 'border-box', marginBottom: 10,
          fontFamily: SANS, fontSize: 13, lineHeight: 1.6, color: O.ink,
          padding: '9px 11px', borderRadius: 10, resize: 'vertical',
          border: `1px solid ${O.hairlineOn}`, background: O.card,
        }}
      />

      {/* THE BUTTONS SHARE A ROW, SO THEY SHARE A SIZE AND FILL IT — the
          founder's standing rule. None is bigger than another: verifying,
          declining and withdrawing are the same weight of decision, and a
          primary-coloured Verify beside a quiet Decline would be the screen
          leaning on the judgement, which is what Immutable Rule 4 forbids at
          the last place it can be broken. */}
      <div style={{ display: 'flex', gap: 10 }}>
        {approved ? (
          <QuietButton
            style={{ flex: 1 }}
            disabled={busy}
            onClick={() => onDecide(business.portfolio_id, 'withdrawn')}
          >
            Withdraw the listing
          </QuietButton>
        ) : (
          <>
            <PrimaryCTA
              style={{ flex: 1 }}
              disabled={busy}
              onClick={() => onDecide(business.portfolio_id, 'verified')}
            >
              Verify this business
            </PrimaryCTA>
            <QuietButton
              style={{ flex: 1 }}
              disabled={busy}
              onClick={() => onDecide(business.portfolio_id, 'declined')}
            >
              Decline
            </QuietButton>
          </>
        )}
      </div>
    </Card>
  );
};

export default function LandlordBusinessQueue() {
  const [state, setState] = useState({ loading: true, error: null, rows: [] });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  // KEYED BY BUSINESS, not one box for the screen. Several cards are open at
  // once, and a single shared box would attach whatever was typed to whichever
  // button was pressed — a reason on the wrong record, permanently.
  const [reasons, setReasons] = useState({});

  const load = async () => {
    const { data, error } = await fetchLandlordBusinessQueue();
    if (error) { setState({ loading: false, error, rows: [] }); return; }
    setState({ loading: false, error: null, rows: data });
  };

  useEffect(() => { load(); }, []);

  const decide = async (portfolioId, decision) => {
    setBusy(true);
    setNote(null);
    const { error } = await decideLandlordBusiness(portfolioId, decision, reasons[portfolioId]);
    setBusy(false);
    if (error) { setNote({ tone: 'critical', text: error }); return; }
    // CLEARED ONLY ON SUCCESS. A reviewer whose decision was refused — no unit
    // proven owned, most likely — should not have to retype what they wrote.
    setReasons((r) => ({ ...r, [portfolioId]: '' }));
    setNote({
      tone: 'info',
      text: {
        verified: 'Verified, with your reason on the record. Tenants can find the business.',
        declined: 'Declined, with your reason on the record. The landlord is told what you wrote.',
        withdrawn: 'Withdrawn, with your reason on the record. The business is no longer listed.',
      }[decision],
    });
    // RE-READ RATHER THAN PATCH IN PLACE. The row's approved_at is the
    // database's to state, and a screen that edits its own copy is a screen
    // that can disagree with the record it is showing.
    await load();
  };

  if (state.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0' }}>
        <Spinner size={16} />
        <span style={{ fontFamily: SANS, fontSize: 12.5, color: O.inkMuted }}>
          Reading the queue…
        </span>
      </div>
    );
  }

  if (state.error) return <Notice tone="critical">{state.error}</Notice>;

  return (
    <div>
      <p style={{
        fontFamily: SANS, fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted,
        margin: '0 0 6px', maxWidth: '74ch',
      }}>
        A landlord asks to be listed; Orchid verifies. Until both are true their
        business cannot be found by a tenant. Check the units proven owned
        against the units they hold — proof is recorded by a compliance seat,
        never claimed by the landlord.
      </p>
      <p style={{
        fontFamily: SANS, fontSize: 11.5, lineHeight: 1.7, color: O.inkFaint,
        margin: '0 0 14px', maxWidth: '74ch',
      }}>
        Your name, the moment and your reason are recorded against the business,
        with the unit counts as they stand right now. A decline is shown to the
        landlord in your own words, so write it for them to read.
      </p>

      {note && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone={note.tone}>{note.text}</Notice>
        </div>
      )}

      {state.rows.length === 0 ? (
        /* AN EMPTY QUEUE IS A SUCCESS, not a blank state to fill. MDS 2.2, the
           same rule the Attention Centre follows. */
        <Notice tone="info">
          No landlord business is waiting to be listed.
        </Notice>
      ) : (
        state.rows.map((b) => (
          <Row
            key={b.portfolio_id}
            business={b}
            onDecide={decide}
            busy={busy}
            reason={reasons[b.portfolio_id]}
            onReason={(id, text) => setReasons((r) => ({ ...r, [id]: text }))}
          />
        ))
      )}
    </div>
  );
}
